"use client"

import type React from "react"

import { Component, type ReactNode } from "react"
import { AlertTriangle, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

/**
 * Error Boundary - Catches React errors and displays a fallback UI
 * Used for graceful error handling in MVP routes
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error for debugging (PII-free)
    console.error("[ErrorBoundary] Component error caught:", {
      message: error.message,
      componentStack: errorInfo.componentStack,
    })
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined })
  }

  handleRefresh = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div
          className="flex flex-col items-center justify-center min-h-[300px] p-6 text-center"
          role="alert"
          aria-live="assertive"
        >
          <div className="w-12 h-12 rounded-full bg-[var(--surface)] border border-[var(--border-subtle)] flex items-center justify-center mb-4">
            <AlertTriangle className="h-6 w-6 text-[var(--text-secondary)]" aria-hidden="true" />
          </div>
          <h2 className="text-lg font-semibold text-foreground mb-2">Something went wrong</h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-sm">
            Try again or refresh the page. If the problem persists, please contact support.
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={this.handleRetry}
              className="gap-2 bg-transparent"
              aria-label="Try again"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Try Again
            </Button>
            <Button variant="ghost" onClick={this.handleRefresh} aria-label="Refresh page">
              Refresh Page
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Inline error fallback for smaller components
 */
export function InlineErrorFallback({
  message = "Failed to load",
  onRetry,
}: {
  message?: string
  onRetry?: () => void
}) {
  return (
    <div
      className="flex items-center gap-2 p-3 rounded-lg bg-[var(--surface)] border border-[var(--border-subtle)] text-sm"
      role="alert"
    >
      <AlertTriangle className="h-4 w-4 text-[var(--text-tertiary)]" aria-hidden="true" />
      <span className="text-muted-foreground">{message}</span>
      {onRetry && (
        <button onClick={onRetry} className="ml-auto text-xs text-[var(--brain)] hover:underline" aria-label="Retry">
          Retry
        </button>
      )}
    </div>
  )
}
